import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Wrench, Shield, Key, KeyRound, Clock, CheckCircle, Lock, DoorOpen, Fingerprint } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../mockData';
import { siteConfig, servicePages, residentialCards, fixedQuoteLine, rates } from '../content/siteContent';
import usePageMeta from '../hooks/usePageMeta';

const iconMap = { Wrench, Shield, Key, KeyRound, Lock, DoorOpen, Fingerprint };

const Residential = () => {
  usePageMeta({
    title: 'Residential Locksmiths North Shore | Shore Doors Locksmiths',
    description:
      "Mobile residential locksmiths on Auckland's North Shore — lockouts, rekeying, key cutting, lock repairs and home security upgrades. Fixed quotes, 12-month guarantee.",
  });

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-gray-950 py-16 sm:py-20 text-white overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-full md:w-3/5" aria-hidden="true">
          <img src="/images/stock/residential-lock-fitting.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/50 to-gray-950/5"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-5">
              Residential locksmiths — mobile across the North Shore
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Commercial-grade lock expertise at your front door — lockouts, rekeying, key
              cutting, lock repairs and replacements, and home security upgrades. Fully mobile,
              fixed quotes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={`tel:${businessInfo.phoneTel}`}>
                <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold text-lg px-8">
                  <Phone className="mr-2" size={20} />
                  Call {businessInfo.phone}
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold text-lg px-8">
                  Get a quote
                </Button>
              </Link>
            </div>
            <p className="text-sm text-gray-300 mt-5">
              {siteConfig.hoursLine} · From ${rates.callout} + GST call-out incl. first 15 minutes · {fixedQuoteLine}
            </p>
            <p className="text-sm mt-3">
              <Link to="/emergency-locksmith" className="text-white font-semibold underline underline-offset-4">
                Locked out right now? Emergency locksmith →
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Service cards */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
            {residentialCards.map((c) => {
              const cfg = servicePages.residential[c.slug];
              const Icon = iconMap[c.icon] || Key;
              const comingSoon = cfg.gated && !siteConfig.locksmithLive;
              const inner = (
                <>
                  {c.bg && (
                    <div className="absolute inset-0" aria-hidden="true">
                      <img src={c.bg} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/60 to-white/25"></div>
                    </div>
                  )}
                  <CardContent className="relative p-8 flex flex-col h-full">
                    <div className="flex items-start justify-between mb-5">
                      <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Icon className="text-blue-600" size={26} />
                      </div>
                      {comingSoon && (
                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1 rounded-full">
                          <Clock size={12} /> Launching soon
                        </span>
                      )}
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">{cfg.title}</h2>
                    <p className="text-gray-700 mb-6 flex-1">{c.blurb}</p>
                    {comingSoon ? (
                      <span className="text-gray-400 font-semibold text-sm">
                        Available once our locksmith starts
                      </span>
                    ) : (
                      <span className="text-blue-600 font-semibold flex items-center gap-2">
                        Learn more <ArrowRight size={16} />
                      </span>
                    )}
                  </CardContent>
                </>
              );
              return comingSoon ? (
                <Card key={c.slug} className="relative overflow-hidden border-gray-200 bg-gray-50/60">{inner}</Card>
              ) : (
                <Link key={c.slug} to={`/residential/${c.slug}`} className="block group">
                  <Card className="relative overflow-hidden border-gray-200 h-full group-hover:border-blue-300 group-hover:shadow-lg transition-all duration-300">{inner}</Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Honesty + trust */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-5">Why a specialist, not a handyman</h2>
            <ul className="space-y-3">
              {[
                'Our team installs commercial lock and access hardware every working day — hospitals, banks, schools',
                'Locks chosen for your doors, fitted square, working smoothly for years',
                '12-month workmanship guarantee on everything we do',
                'Fixed quote before any extra work — no meter running behind your back',
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={18} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <Card className="border-gray-200 bg-white h-fit overflow-hidden">
            <div className="grid grid-cols-2 gap-0">
              <img
                src="/images/stock/residential-banner-red-door.jpg"
                alt="High-end residential entrance door"
                className="w-full h-44 object-cover"
              />
              <img
                src="/images/stock/residential-fancy-lock.jpg"
                alt="Premium designer lock hardware"
                className="w-full h-44 object-cover"
              />
            </div>
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Bespoke & high-end installs</h3>
              <p className="text-gray-700 mb-3">
                Designer hardware, architectural doors and premium builds are where our commercial
                background really shows. We source and fit high-end and custom locking hardware —
                matched to the joinery, finished cleanly, working perfectly for years.
              </p>
              <p className="text-gray-700">
                Got a special door or a specific look in mind?{' '}
                <Link to="/contact" className="text-blue-600 font-semibold">
                  Talk to us about a bespoke install →
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* A200 accessibility callout */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Need a door that opens itself?
              </h2>
              <p className="text-gray-700 mb-5">
                We're Auckland's only certified installer of the AHS A200 door actuator —
                automatic door opening for accessibility, fitted regularly for disabled customers
                through ACC.
              </p>
              <Link to="/commercial/door-automation" className="text-blue-600 font-semibold">
                Door automation & accessibility →
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Residential;
